import { useEffect, useRef, useState } from "react";
import { Button, Checkbox, Input, message, Table, Spin } from "antd";
import { hasSubstring } from "../../extensions/helper";
import { serverFetch } from "../../server";
import { isMobile, menuSize } from "../../extensions/design";
import { tableRenderDateTime } from "../../extensions/table-render";
import Company from "./company";
import CompanyItem from "./company-item";
import "./companies.css";

const SORT_STORAGE_KEY = "companies-sort";

const COLUMN_TYPES = {
  active: "boolean",
  id: "number",
  name: "string",
  createdOn: "date",
  email: "string",
  domain: "string",
  prcComission: "number",
  paymentPrc: "number",
  comissionIncluded: "boolean",
  gmt: "number",
  companyTime: "date",
};

const loadSort = () => {
  try {
    const data = localStorage.getItem(SORT_STORAGE_KEY);
    if (!data) return { field: null, order: null };
    const parsed = JSON.parse(data);
    if (!parsed?.field || (parsed.order !== "ascend" && parsed.order !== "descend")) {
      return { field: null, order: null };
    }
    return { field: parsed.field, order: parsed.order };
  } catch {
    return { field: null, order: null };
  }
};

const saveSort = (sort) => {
  try {
    if (!sort.field || !sort.order) {
      localStorage.removeItem(SORT_STORAGE_KEY);
      return;
    }
    localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort));
  } catch {
    // ignore quota / private mode
  }
};

const compareValues = (a, b, type) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (type === "number" || type === "boolean") return Number(a) - Number(b);
  if (type === "date") {
    const timeA = new Date(a).getTime();
    const timeB = new Date(b).getTime();
    if (Number.isNaN(timeA) && Number.isNaN(timeB)) return 0;
    if (Number.isNaN(timeA)) return -1;
    if (Number.isNaN(timeB)) return 1;
    return timeA - timeB;
  }
  return String(a).localeCompare(String(b), "ru", { numeric: true, sensitivity: "base" });
};

const makeSorter = (field) => (a, b) => compareValues(a[field], b[field], COLUMN_TYPES[field] ?? "string");

const CompaniesList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState();
  const [filter, setFilter] = useState({ showHidden: false, serchText: "", vid: null });
  const [sort, setSort] = useState(loadSort);
  const divRef = useRef(null);

  const getData = () => {
    setLoading(true);
    serverFetch("company")
      .then(data => {
        setItems(data);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }

  useEffect(getData, []);

  const formatTime = (value) => {
    if (!value) {
      return "";
    }

    const match = String(value).match(/T(\d{2}):(\d{2}):(\d{2})/);
    if (!match) {
      return String(value);
    }

    const [, hour, minute, second] = match;
    return `${hour}:${minute}:${second}`;
  };

  const columns = [
    {title: "Акт.", dataIndex: "active", render: value => <Checkbox checked={value} />, width: 50, sorter: makeSorter("active"), sortOrder: sort.field === "active" ? sort.order : null},
    {title: "#", dataIndex: "id", sorter: makeSorter("id"), sortOrder: sort.field === "id" ? sort.order : null},
    {title: "Наименование", dataIndex: "name", sorter: makeSorter("name"), sortOrder: sort.field === "name" ? sort.order : null},
    {title: "Регистрация", dataIndex: "createdOn", render: tableRenderDateTime, sorter: makeSorter("createdOn"), sortOrder: sort.field === "createdOn" ? sort.order : null},
    {title: "Email", dataIndex: "email", sorter: makeSorter("email"), sortOrder: sort.field === "email" ? sort.order : null},
    {title: "Домен", dataIndex: "domain", sorter: makeSorter("domain"), sortOrder: sort.field === "domain" ? sort.order : null},
    {title: "Комиссия, %", dataIndex: "prcComission", align: "right", sorter: makeSorter("prcComission"), sortOrder: sort.field === "prcComission" ? sort.order : null},
    {title: "Комиссия по договору, %", dataIndex: "paymentPrc", align: "right", sorter: makeSorter("paymentPrc"), sortOrder: sort.field === "paymentPrc" ? sort.order : null},
    {title: "Включена", dataIndex: "comissionIncluded", render: value => <Checkbox checked={value} />, width: 100, sorter: makeSorter("comissionIncluded"), sortOrder: sort.field === "comissionIncluded" ? sort.order : null},
    {title: "GMT", dataIndex: "gmt", align: "right", sorter: makeSorter("gmt"), sortOrder: sort.field === "gmt" ? sort.order : null},
    {title: "Время", dataIndex: "companyTime", align: "right", render: value => formatTime(value), sorter: makeSorter("companyTime"), sortOrder: sort.field === "companyTime" ? sort.order : null},
  ]

  const onSortChange = (_pagination, _filters, sorter) => {
    const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const next = {
      field: nextSorter?.order ? nextSorter.field : null,
      order: nextSorter?.order ?? null,
    };
    setSort(next);
    saveSort(next);
  };

  const onClose = (refresh) => {
    setEditId();
    refresh && getData();
  }

  const divItem = editId != null && <Company id={editId} onClose={onClose} />
  const headerSize =  divRef.current?.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
  const tableHeight = divRef.current ? (document.documentElement.offsetHeight - headerSize - divRef.current.getBoundingClientRect().top - window.scrollY) : 0;
  const tableWidth = (document.documentElement.offsetWidth - divRef.current?.getBoundingClientRect().left) || 0;

  const onChangeFilter = value => setFilter({ ...filter, ...value });

  const filtered = items.filter(item => {
    if (!filter.showHidden && !item.active) return false;
    if (!hasSubstring(filter.serchText, item.name)) return false;
    return true;
  })

  const sorted = (() => {
    if (!sort.field || !sort.order) return filtered;
    const type = COLUMN_TYPES[sort.field] ?? "string";
    return [...filtered].sort((a, b) => {
      const result = compareValues(a[sort.field], b[sort.field], type);
      return sort.order === "descend" ? -result : result;
    });
  })();

  const divTools = (
    <div className="sport-form-title">
      <Checkbox checked={filter.showHidden} onChange={e => onChangeFilter({ showHidden: e.target.checked })}>
        Показывать неактивные
      </Checkbox>
      <Input 
        placeholder="Поиск" 
        value={filter.serchText} 
        onChange={e => onChangeFilter({serchText: e.target.value})} 
        allowClear 
        style={isMobile ? {flex: 1} : {maxWidth: 200}}
      />
      <Button onClick={getData}>Обновить</Button>
    </div>
  );

  const divTable = isMobile ? (
    <div 
      className="company-items" 
      style={{
        height: window.innerHeight - menuSize, 
        overflowY: "scroll", 
        overflowX: "clip"
      }}
    >
      {loading ? (
        <div style={{textAlign: 'center', padding: '20px'}}>
          <Spin />
        </div>
      ) : (
        sorted.map(item => (
          <CompanyItem 
            key={item.id} 
            item={item} 
            onClick={() => setEditId(item.id)}
            isSelected={editId === item.id}
          />
        ))
      )}
    </div>
  ) : (
    <div ref={divRef} style={{width: tableWidth}}>
      <Table
        className="companies-table"
        dataSource={sorted}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
        showSorterTooltip={false}
        scroll={{ y: tableHeight }}
        rowSelection={{ type: 'radio', selectedRowKeys: [editId], renderCell: checked => <div className={checked ? "list-indicator" : ""} /> }}
        onRow={(record) => ({onClick: () => {setEditId(record.id)}})}
        onChange={onSortChange}
      /> 
    </div>
  );

  return (
    <div className="sport-form-content" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {divTools}
      {divTable}
      {divItem}
    </div>
  );
}

export default CompaniesList;
